import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Carrusel from "../components/Carrusel";
import CategoriasGrid from "../components/CategoriasGrid";
import { FaBuilding, FaHandsHelping, FaTruck, FaUpload } from "react-icons/fa";
import AOS from "aos";
import "aos/dist/aos.css";
import { auth } from "../utils/firebaseConfig";
import { onAuthStateChanged } from "firebase/auth";
import CarruselProductos from "../components/CarruselProductos";

export default function Inicio() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [subiendo, setSubiendo] = useState(false);
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [vistosRecientemente, setVistosRecientemente] = useState([]);
  const [recomendados, setRecomendados] = useState([]);

  useEffect(() => {
    AOS.init({ duration: 700, once: true });
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUser(user);
        setIsAdmin(user.email === "admin@empresa.com");
      } else {
        setUser(null);
        setIsAdmin(false);
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const versionActual = "20250824";
    const guardados = localStorage.getItem("productos");
    const vistos = JSON.parse(localStorage.getItem("vistos") || "[]");

    let productos = [];

    if (guardados) {
      try {
        const datos = JSON.parse(guardados);
        const datosSonValidos =
          Array.isArray(datos) &&
          datos.length > 0 &&
          datos.every(
            (p) => typeof p === "object" && p.imagen?.includes(`?v=${versionActual}`)
          );

        if (!datosSonValidos) {
          console.warn("🚫 Datos en localStorage inválidos o vacíos. Forzando recarga.");
          localStorage.removeItem("productos");
          window.location.reload();
          return;
        }

        productos = datos;
      } catch (err) {
        console.error("❌ Error al leer productos del localStorage:", err);
        localStorage.removeItem("productos");
        window.location.reload();
        return;
      }

      setVistosRecientemente(vistos);
      const vistosSKUs = new Set(vistos.map((p) => p.sku));
      const noVistos = productos.filter((p) => !vistosSKUs.has(p.sku));
      const aleatorios = noVistos.sort(() => 0.5 - Math.random()).slice(0, 10);
      setRecomendados(aleatorios);
    }
  }, []);

  return (
    <div className="bg-gray-100 min-h-screen scroll-smooth">

      {/* ── CARRUSEL ── flujo limpio, sin margin negativo */}
      <div className="w-full">
        <Carrusel />
      </div>

      {/* ── BARRA DE CARGA (subiendo CSV) ── */}
      {subiendo && (
        <div className="w-full h-1 bg-amber-400 animate-pulse" />
      )}

      {/* ── CONTENIDO PRINCIPAL ── */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 flex flex-col gap-12">

        {/* ── PANEL ADMIN ── */}
        {isAdmin && (
          <div
            data-aos="fade-down"
            className="flex items-center justify-between gap-4 bg-white border border-gray-200 rounded-xl px-5 py-4"
          >
            <div className="flex items-center gap-3">
              <span className="text-xs font-semibold tracking-widest uppercase text-[#40767b] bg-[#f0f7f7] border border-[#c8dfe0] rounded-md px-2.5 py-1">
                Admin
              </span>
              <p className="text-sm text-gray-500">Gestión de productos y catálogo</p>
            </div>
            <button
              onClick={() => {
                setSubiendo(true);
                navigate("/subircsv");
              }}
              className="flex items-center gap-2 px-4 py-2 bg-[#40767b] hover:bg-[#335f63] text-white text-sm font-medium rounded-lg transition-colors"
            >
              <FaUpload className="text-xs" />
              Subir CSV
            </button>
          </div>
        )}

        {/* ── CATEGORÍAS ── */}
        <section data-aos="fade-up">
          <SectionHeader title="Explorar categorías" />
          <CategoriasGrid
            currentCategory=""
            onCategoryChange={(cat) => {
              navigate(`/catalogo?categoria=${encodeURIComponent(cat)}&scroll=1`, {
                state: { fromHomeCategory: true },
              });
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          />
        </section>

        {/* ── RECOMENDADOS ── */}
        {recomendados.length > 0 && (
          <section data-aos="fade-up">
            <SectionHeader title="Recomendados para ti" count={recomendados.length} />
            <CarruselProductos productos={recomendados} />
          </section>
        )}

        {/* ── VISTOS RECIENTEMENTE ── */}
        {vistosRecientemente.length > 0 && (
          <section data-aos="fade-up">
            <SectionHeader title="Vistos recientemente" />
            <CarruselProductos productos={vistosRecientemente} />
          </section>
        )}

        {/* ── QUIÉNES SOMOS ── */}
        <section data-aos="fade-up">
          <SectionHeader title="Quiénes somos" />

          <div className="rounded-2xl overflow-hidden border border-gray-200">

            {/* Header sólido con color de marca */}
            <div className="bg-[#40767b] px-8 py-8 text-white">
              <p className="text-xs font-semibold tracking-widest uppercase text-white/60 mb-2">
                Empresa guatemalteca
              </p>
              <h2 className="text-2xl font-bold mb-3">
                Más de una década sirviendo a empresas
              </h2>
              <p className="text-sm leading-relaxed text-white/80 max-w-2xl">
                Somos una empresa guatemalteca dedicada a proveer productos de calidad
                para oficinas, empresas y organizaciones en todo el país. Nuestro
                compromiso es brindarte la mejor experiencia de compra.
              </p>
            </div>

            {/* Cuerpo blanco con tres columnas */}
            <div className="bg-white grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-gray-100">

              <div
                data-aos="fade-up"
                data-aos-delay="100"
                className="flex flex-col gap-3 p-7"
              >
                <div className="w-11 h-11 rounded-xl bg-[#f0f7f7] flex items-center justify-center text-[#40767b]">
                  <FaBuilding className="text-lg" />
                </div>
                <h4 className="font-semibold text-gray-800 text-sm">Empresa consolidada</h4>
                <p className="text-xs text-gray-500 leading-relaxed">
                  Años de experiencia respaldando a cientos de clientes en todo Guatemala.
                </p>
              </div>

              <div
                data-aos="fade-up"
                data-aos-delay="200"
                className="flex flex-col gap-3 p-7"
              >
                <div className="w-11 h-11 rounded-xl bg-[#f0f7f7] flex items-center justify-center text-[#40767b]">
                  <FaHandsHelping className="text-lg" />
                </div>
                <h4 className="font-semibold text-gray-800 text-sm">Atención personalizada</h4>
                <p className="text-xs text-gray-500 leading-relaxed">
                  Escuchamos tus necesidades y encontramos la solución ideal para tu negocio.
                </p>
              </div>

              <div
                data-aos="fade-up"
                data-aos-delay="300"
                className="flex flex-col gap-3 p-7"
              >
                <div className="w-11 h-11 rounded-xl bg-[#f0f7f7] flex items-center justify-center text-[#40767b]">
                  <FaTruck className="text-lg" />
                </div>
                <h4 className="font-semibold text-gray-800 text-sm">Entrega rápida</h4>
                <p className="text-xs text-gray-500 leading-relaxed">
                  Llevamos tus productos directamente a tu empresa con rapidez y cuidado.
                </p>
              </div>

            </div>
          </div>
        </section>

      </div>
    </div>
  );
}

/* ── Componente auxiliar reutilizable para encabezados de sección ── */
function SectionHeader({ title, count }) {
  return (
    <div className="flex items-center gap-3 mb-5">
      <h2 className="text-sm font-semibold text-[#40767b] whitespace-nowrap">{title}</h2>
      {count !== undefined && (
        <span className="text-xs text-gray-400 bg-gray-100 rounded-full px-2 py-0.5">
          {count}
        </span>
      )}
      <div className="flex-1 h-px bg-gray-200" />
    </div>
  );
}
